import React from "react";
import { useTranslation } from "react-i18next";
import {
    Actions,
    Box,
    Button,
    Checkbox,
    Column,
    Columns,
    Divider,
    Dropdown,
    Drawer,
    Stack,
    Text,
    TextField,
} from "braid-design-system";
import { useToast } from "../../hooks/useToast";
import { getAllCustomers } from "../../services/customerService";
import { getAllCustomerGroups, CustomerGroupResponse } from "../../services/customerGroupService";
import NumericTextField from "../NumericTextField";

export interface CustomerFormData {
    cus_accno: string;
    cus_code: string;
    cus_name: string;
    cus_title: string;
    cus_groupid: number | null;
    cus_active: boolean;
    cus_add1: string;
    cus_add2: string;
    cus_add3: string;
    cus_add4: string;
    cus_tep1: string;
    cus_tep2: string;
    cus_email: string;
    cus_dob: string;
    cus_prof: string;
    cus_spouse: string;
    cus_creditlimit: number | null;
    cus_creditperiod: number | null;
    cus_opbal: number | null;
    cus_purchase: number | null;
    cus_returns: number | null;
    cus_creditnote: number | null;
    cus_debitnote: number | null;
    cus_payment: number | null;
    cus_balance: number | null;
    cus_loyaltyactive: boolean;
    cus_openpoint: number | null;
    cus_earnepoint: number | null;
    cus_redeempoint: number | null;
    cus_points: number | null;
}

export type CustomerToEdit = {
    cus_id?: number | null;
} & Partial<{
    [K in keyof CustomerFormData]: CustomerFormData[K] | null;
}>;

interface FormErrors {
    cus_accno?: string;
    cus_code?: string;
    cus_name?: string;
    cus_title?: string;
    cus_groupid?: string;
    cus_add1?: string;
    cus_add2?: string;
    cus_add3?: string;
    cus_add4?: string;
    cus_tep1?: string;
    cus_tep2?: string;
    cus_email?: string;
    cus_dob?: string;
    cus_prof?: string;
    cus_spouse?: string;
    cus_creditlimit?: string;
    cus_creditperiod?: string;
    cus_opbal?: string;
    cus_purchase?: string;
    cus_returns?: string;
    cus_creditnote?: string;
    cus_debitnote?: string;
    cus_payment?: string;
    cus_balance?: string;
    cus_openpoint?: string;
    cus_earnepoint?: string;
    cus_redeempoint?: string;
    cus_points?: string;
}

interface CreateUpdateCustomerDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    customerToEdit?: CustomerToEdit;
    onSave: (customerData: CustomerFormData, editingCustomer?: CustomerToEdit) => Promise<void> | void;
    onCustomerSaved?: () => void;
    isReadOnly?: boolean;
}

const defaultFormData: CustomerFormData = {
    cus_accno: "",
    cus_code: "",
    cus_name: "",
    cus_title: "",
    cus_groupid: null,
    cus_active: true,
    cus_add1: "",
    cus_add2: "",
    cus_add3: "",
    cus_add4: "",
    cus_tep1: "",
    cus_tep2: "",
    cus_email: "",
    cus_dob: "",
    cus_prof: "",
    cus_spouse: "",
    cus_creditlimit: null,
    cus_creditperiod: null,
    cus_opbal: null,
    cus_purchase: null,
    cus_returns: null,
    cus_creditnote: null,
    cus_debitnote: null,
    cus_payment: null,
    cus_balance: null,
    cus_loyaltyactive: false,
    cus_openpoint: null,
    cus_earnepoint: null,
    cus_redeempoint: null,
    cus_points: null,
};

const CreateUpdateCustomerDrawer: React.FC<CreateUpdateCustomerDrawerProps> = ({
    isOpen,
    onClose,
    customerToEdit,
    onSave,
    onCustomerSaved,
    isReadOnly = false,
}) => {
    const { t } = useTranslation();
    const toast = useToast();

    const [formData, setFormData] = React.useState<CustomerFormData>(defaultFormData);
    const [errors, setErrors] = React.useState<FormErrors>({});
    const [submitting, setSubmitting] = React.useState(false);
    const [showMoreDetails, setShowMoreDetails] = React.useState(false);
    const [customerGroups, setCustomerGroups] = React.useState<CustomerGroupResponse[]>([]);
    const isEditing = Boolean(customerToEdit);

    React.useEffect(() => {
        if (!isOpen) {
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const groups = await getAllCustomerGroups();
                if (!cancelled) {
                    setCustomerGroups(groups);
                }
            } catch (error: any) {
                if (!cancelled) {
                    toast.showError(error?.message || "Failed to load customer groups");
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isOpen]);

    React.useEffect(() => {
        if (!isOpen) {
            return;
        }

        if (customerToEdit) {
            const rawGroupId = customerToEdit.cus_groupid;
            const normalizedGroupId =
                rawGroupId === null || rawGroupId === undefined
                    ? null
                    : Number(rawGroupId as number | string);

            setFormData({
                cus_accno: customerToEdit.cus_accno ?? "",
                cus_code: customerToEdit.cus_code ?? "",
                cus_name: customerToEdit.cus_name ?? "",
                cus_title: customerToEdit.cus_title ?? "",
                cus_groupid: Number.isFinite(normalizedGroupId) ? normalizedGroupId : null,
                cus_active: customerToEdit.cus_active ?? true,
                cus_add1: customerToEdit.cus_add1 ?? "",
                cus_add2: customerToEdit.cus_add2 ?? "",
                cus_add3: customerToEdit.cus_add3 ?? "",
                cus_add4: customerToEdit.cus_add4 ?? "",
                cus_tep1: customerToEdit.cus_tep1 ?? "",
                cus_tep2: customerToEdit.cus_tep2 ?? "",
                cus_email: customerToEdit.cus_email ?? "",
                cus_dob: customerToEdit.cus_dob ?? "",
                cus_prof: customerToEdit.cus_prof ?? "",
                cus_spouse: customerToEdit.cus_spouse ?? "",
                cus_loyaltyactive: Boolean(customerToEdit.cus_loyaltyactive),
                cus_creditlimit: customerToEdit.cus_creditlimit ?? null,
                cus_creditperiod: customerToEdit.cus_creditperiod ?? null,
                cus_opbal: customerToEdit.cus_opbal ?? null,
                cus_purchase: customerToEdit.cus_purchase ?? null,
                cus_returns: customerToEdit.cus_returns ?? null,
                cus_creditnote: customerToEdit.cus_creditnote ?? null,
                cus_debitnote: customerToEdit.cus_debitnote ?? null,
                cus_payment: customerToEdit.cus_payment ?? null,
                cus_balance: customerToEdit.cus_balance ?? null,
                cus_openpoint: customerToEdit.cus_openpoint ?? null,
                cus_earnepoint: customerToEdit.cus_earnepoint ?? null,
                cus_redeempoint: customerToEdit.cus_redeempoint ?? null,
                cus_points: customerToEdit.cus_points ?? null,
            });
            // Always show all fields when editing
            setShowMoreDetails(true);
        } else {
            setFormData(defaultFormData);
            setShowMoreDetails(false);
        }

        setErrors({});
    }, [isOpen, customerToEdit]);

    const clearError = (key: keyof FormErrors) => {
        if (errors[key]) {
            setErrors((prev) => ({ ...prev, [key]: undefined }));
        }
    };

    const updateText = (key: keyof CustomerFormData, value: string, maxLength?: number) => {
        const normalizedValue = typeof maxLength === "number" ? value.slice(0, maxLength) : value;
        setFormData((prev) => ({ ...prev, [key]: normalizedValue }));
        clearError(key as keyof FormErrors);
    };

    const updateNumber = (key: keyof CustomerFormData, value: string) => {
        const parsedValue = value === "" || value === "-" ? null : Number(value);
        setFormData((prev) => ({ ...prev, [key]: parsedValue }));
        clearError(key as keyof FormErrors);
    };

    const flagNumbersOnly = (key: keyof FormErrors) => () => {
        setErrors((prev) => ({
            ...prev,
            [key]: t("page.customer_management.drawer.validation.numbers_only", {
                defaultValue: "Please enter numbers only",
            }),
        }));
    };

    const updateBoolean = (key: keyof CustomerFormData, value: boolean) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
    };

    const validate = () => {
        const nextErrors: FormErrors = {};

        const checkMaxLength = (key: keyof FormErrors, value: string | null | undefined, max: number) => {
            if ((value || "").length > max) {
                nextErrors[key] = t("page.customer_management.drawer.validation.max_length", {
                    defaultValue: "Maximum {{count}} characters allowed",
                    count: max,
                });
            }
        };

        const checkNumeric = (key: keyof FormErrors, value: number | null) => {
            if (value === null) {
                return;
            }

            if (!Number.isFinite(value)) {
                nextErrors[key] = t("page.customer_management.drawer.validation.invalid_number", {
                    defaultValue: "Invalid numeric value",
                });
            }
        };

        if (!formData.cus_code.trim()) {
            nextErrors.cus_code = t("page.customer_management.drawer.validation.required", {
                defaultValue: "Required",
            });
        }

        if (!formData.cus_name.trim()) {
            nextErrors.cus_name = t("page.customer_management.drawer.validation.required", {
                defaultValue: "Required",
            });
        }

        checkMaxLength("cus_code", formData.cus_code, 32);
        checkMaxLength("cus_name", formData.cus_name, 60);
        checkMaxLength("cus_title", formData.cus_title, 10);
        checkMaxLength("cus_add1", formData.cus_add1, 40);
        checkMaxLength("cus_add2", formData.cus_add2, 40);
        checkMaxLength("cus_add3", formData.cus_add3, 40);
        checkMaxLength("cus_add4", formData.cus_add4, 40);
        checkMaxLength("cus_tep1", formData.cus_tep1, 30);
        checkMaxLength("cus_tep2", formData.cus_tep2, 30);
        checkMaxLength("cus_email", formData.cus_email, 40);
        checkMaxLength("cus_dob", formData.cus_dob, 19);
        checkMaxLength("cus_prof", formData.cus_prof, 40);
        checkMaxLength("cus_spouse", formData.cus_spouse, 40);

        const checkNumericMaxLength = (key: keyof FormErrors, value: number | null, max = 21) => {
            if (value === null || value === undefined) {
                return;
            }

            if (String(value).length > max) {
                nextErrors[key] = t("page.customer_management.drawer.validation.max_length", {
                    defaultValue: "Maximum {{count}} characters allowed",
                    count: max,
                });
            }
        };

        if (formData.cus_email.trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.cus_email.trim())) {
                nextErrors.cus_email = t("page.customer_management.drawer.validation.invalid_email", {
                    defaultValue: "Invalid email address",
                });
            }
        }

        [
            ["cus_creditlimit", formData.cus_creditlimit],
            ["cus_creditperiod", formData.cus_creditperiod],
            ["cus_opbal", formData.cus_opbal],
            ["cus_purchase", formData.cus_purchase],
            ["cus_returns", formData.cus_returns],
            ["cus_creditnote", formData.cus_creditnote],
            ["cus_debitnote", formData.cus_debitnote],
            ["cus_payment", formData.cus_payment],
            ["cus_balance", formData.cus_balance],
            ["cus_openpoint", formData.cus_openpoint],
            ["cus_earnepoint", formData.cus_earnepoint],
            ["cus_redeempoint", formData.cus_redeempoint],
            ["cus_points", formData.cus_points],
        ].forEach(([key, value]) => {
            checkNumeric(key as keyof FormErrors, value as number | null);
            checkNumericMaxLength(key as keyof FormErrors, value as number | null, 21);
        });

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (isReadOnly) {
            return;
        }

        if (submitting) {
            return;
        }

        if (!validate()) {
            toast.showError(
                t("page.customer_management.drawer.validation.fix_errors", {
                    defaultValue: "Please fix validation errors",
                }),
            );
            return;
        }

        // Check for duplicate customer code
        if (formData.cus_code.trim()) {
            try {
                const existingCustomers = await getAllCustomers();
                const duplicate = existingCustomers.find(
                    (c) =>
                        (c.cus_code || "").trim().toUpperCase() === formData.cus_code.trim().toUpperCase() &&
                        c.cus_id !== customerToEdit?.cus_id
                );
                if (duplicate) {
                    setErrors((prev) => ({
                        ...prev,
                        cus_code: t("page.customer_management.drawer.validation.duplicate_code", {
                            defaultValue: "This customer code already exists",
                        }),
                    }));
                    toast.showError(
                        t("page.customer_management.drawer.validation.duplicate_code", {
                            defaultValue: "This customer code already exists",
                        })
                    );
                    return;
                }
            } catch (error: any) {
                toast.showError(error?.message || "Failed to validate customer code");
                return;
            }
        }

        try {
            setSubmitting(true);
            await onSave(formData, customerToEdit);

            if (onCustomerSaved) {
                onCustomerSaved();
            }

            toast.showSuccess(
                customerToEdit
                    ? t("page.customer_management.drawer.success.updated", {
                          defaultValue: "Customer updated successfully",
                      })
                    : t("page.customer_management.drawer.success.created", {
                          defaultValue: "Customer created successfully",
                      }),
            );

            onClose();
        } catch (error: any) {
            toast.showError(
                error?.message ||
                    t("page.customer_management.drawer.error.save_failed", {
                        defaultValue: "Failed to save customer",
                    }),
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Drawer
            open={isOpen}
            onClose={onClose}
            title={
                customerToEdit
                    ? t("page.customer_management.drawer.title.update", { defaultValue: "Update Customer" })
                    : t("page.customer_management.drawer.title.create", { defaultValue: "Create New Customer" })
            }
            width="medium"
        >
            <form onSubmit={handleSubmit} autoComplete="off">
                <Box padding="large">
                    <Stack space="large">
                        {/* Always show required fields: Customer Code, Customer Name, Telephone 1 */}
                        <Text weight="strong" tone="info">
                            {t("page.customer_management.drawer.section.required", {
                                defaultValue: "Required Details",
                            })}
                        </Text>
                            <Stack space="small">
                                <TextField
                                    label={t("page.customer_management.drawer.label.cus_code", {
                                        defaultValue: "Customer Code*",
                                    })}
                                    value={formData.cus_code}
                                    tone={errors.cus_code ? "critical" : undefined}
                                    message={errors.cus_code}
                                    onChange={(e) => updateText("cus_code", e.currentTarget.value, 32)}
                                />
                                <TextField
                                    label={t("page.customer_management.drawer.label.cus_name", {
                                        defaultValue: "Customer Name*",
                                    })}
                                    value={formData.cus_name}
                                    tone={errors.cus_name ? "critical" : undefined}
                                    message={errors.cus_name}
                                    onChange={(e) => updateText("cus_name", e.currentTarget.value, 60)}
                                />
                                <NumericTextField
                                    label={t("page.customer_management.drawer.label.cus_tep1", {
                                        defaultValue: "Telephone 1",
                                    })}
                                    mode="integer"
                                    maxLength={30}
                                    value={formData.cus_tep1}
                                    tone={errors.cus_tep1 ? "critical" : undefined}
                                    message={errors.cus_tep1}
                                    onChange={(value) => updateText("cus_tep1", value)}
                                    onInvalid={flagNumbersOnly("cus_tep1")}
                                />
                            </Stack>

                        {/* "More Details" toggle - only shown when creating a new customer */}
                        {!isEditing && !showMoreDetails && (
                            <Actions>
                                <Button
                                    variant="ghost"
                                    onClick={() => setShowMoreDetails(true)}
                                >
                                    {t("page.customer_management.drawer.button.more_details", {
                                        defaultValue: "+ More Details",
                                    })}
                                </Button>
                            </Actions>
                        )}

                        {/* Full details section - shown when editing or when "More Details" is clicked */}
                        {(isEditing || showMoreDetails) && (
                            <>
                                <Divider />

                                <Text weight="strong" tone="info">
                                    {t("page.customer_management.drawer.section.basic", {
                                        defaultValue: "Additional Details",
                                    })}
                                </Text>
                                <Columns space="small" collapseBelow="desktop">
                                    <Column width="1/2">
                                        <Stack space="small">
                                            <TextField
                                                label={t("page.customer_management.drawer.label.cus_accno", {
                                                    defaultValue: "Account Number",
                                                })}
                                                value={formData.cus_accno}
                                                tone={errors.cus_accno ? "critical" : undefined}
                                                message={errors.cus_accno}
                                                onChange={(e) => updateText("cus_accno", e.currentTarget.value, 20)}
                                            />
                                            <Dropdown
                                                label={t("page.customer_management.drawer.label.cus_title", {
                                                    defaultValue: "Title",
                                                })}
                                                value={formData.cus_title}
                                                tone={errors.cus_title ? "critical" : undefined}
                                                message={errors.cus_title}
                                                onChange={(e) => updateText("cus_title", e.currentTarget.value, 10)}
                                            >
                                                <option value="">{t("page.customer_management.drawer.title.select", { defaultValue: "Select Title" })}</option>
                                                <option value="Mr.">Mr.</option>
                                                <option value="Miss">Miss</option>
                                                <option value="Mrs.">Mrs.</option>
                                                <option value="Dr.">Dr.</option>
                                            </Dropdown>
                                            <Dropdown
                                                label={t("page.customer_management.drawer.label.cus_groupid", {
                                                    defaultValue: "Group",
                                                })}
                                                value={formData.cus_groupid === null ? "" : String(formData.cus_groupid)}
                                                tone={errors.cus_groupid ? "critical" : undefined}
                                                message={errors.cus_groupid}
                                                onChange={(e) => {
                                                    const rawValue = e.currentTarget.value;
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        cus_groupid: rawValue === "" ? null : Number(rawValue),
                                                    }));
                                                    clearError("cus_groupid");
                                                }}
                                            >
                                                {[
                                                    <option key="" value="">
                                                        {t("page.customer_management.drawer.group.select", {
                                                            defaultValue: "Select Group",
                                                        })}
                                                    </option>,
                                                    ...customerGroups.map((group) => (
                                                        <option key={group.id} value={group.id}>
                                                            {group.group_name}
                                                        </option>
                                                    )),
                                                ]}
                                            </Dropdown>
                                            <TextField
                                                label={t("page.customer_management.drawer.label.cus_email", {
                                                    defaultValue: "Email",
                                                })}
                                                value={formData.cus_email}
                                                tone={errors.cus_email ? "critical" : undefined}
                                                message={errors.cus_email}
                                                onChange={(e) => updateText("cus_email", e.currentTarget.value, 40)}
                                            />
                                            <TextField
                                                label={t("page.customer_management.drawer.label.cus_dob", {
                                                    defaultValue: "Date of Birth",
                                                })}
                                                value={formData.cus_dob}
                                                tone={errors.cus_dob ? "critical" : undefined}
                                                message={errors.cus_dob}
                                                onChange={(e) => updateText("cus_dob", e.currentTarget.value, 19)}
                                            />
                                            <TextField
                                                label={t("page.customer_management.drawer.label.cus_prof", {
                                                    defaultValue: "Profession",
                                                })}
                                                value={formData.cus_prof}
                                                tone={errors.cus_prof ? "critical" : undefined}
                                                message={errors.cus_prof}
                                                onChange={(e) => updateText("cus_prof", e.currentTarget.value, 40)}
                                            />
                                        </Stack>
                                    </Column>
                                    <Column width="1/2">
                                        <Stack space="small">
                                            <TextField
                                                label={t("page.customer_management.drawer.label.cus_spouse", {
                                                    defaultValue: "Spouse Name",
                                                })}
                                                value={formData.cus_spouse}
                                                tone={errors.cus_spouse ? "critical" : undefined}
                                                message={errors.cus_spouse}
                                                onChange={(e) => updateText("cus_spouse", e.currentTarget.value, 40)}
                                            />
                                            <NumericTextField
                                                label={t("page.customer_management.drawer.label.cus_tep2", {
                                                    defaultValue: "Telephone 2",
                                                })}
                                                mode="integer"
                                                maxLength={30}
                                                value={formData.cus_tep2}
                                                tone={errors.cus_tep2 ? "critical" : undefined}
                                                message={errors.cus_tep2}
                                                onChange={(value) => updateText("cus_tep2", value)}
                                                onInvalid={flagNumbersOnly("cus_tep2")}
                                            />
                                            <TextField
                                                label={t("page.customer_management.drawer.label.cus_add1", {
                                                    defaultValue: "Address Line 1",
                                                })}
                                                value={formData.cus_add1}
                                                tone={errors.cus_add1 ? "critical" : undefined}
                                                message={errors.cus_add1}
                                                onChange={(e) => updateText("cus_add1", e.currentTarget.value, 40)}
                                            />
                                            <TextField
                                                label={t("page.customer_management.drawer.label.cus_add2", {
                                                    defaultValue: "Address Line 2",
                                                })}
                                                value={formData.cus_add2}
                                                tone={errors.cus_add2 ? "critical" : undefined}
                                                message={errors.cus_add2}
                                                onChange={(e) => updateText("cus_add2", e.currentTarget.value, 40)}
                                            />
                                            <TextField
                                                label={t("page.customer_management.drawer.label.cus_add3", {
                                                    defaultValue: "Address Line 3",
                                                })}
                                                value={formData.cus_add3}
                                                tone={errors.cus_add3 ? "critical" : undefined}
                                                message={errors.cus_add3}
                                                onChange={(e) => updateText("cus_add3", e.currentTarget.value, 40)}
                                            />
                                            <TextField
                                                label={t("page.customer_management.drawer.label.cus_add4", {
                                                    defaultValue: "Address Line 4",
                                                })}
                                                value={formData.cus_add4}
                                                tone={errors.cus_add4 ? "critical" : undefined}
                                                message={errors.cus_add4}
                                                onChange={(e) => updateText("cus_add4", e.currentTarget.value, 40)}
                                            />
                                        </Stack>
                                    </Column>
                                </Columns>

                                <Divider />

                                <Text weight="strong" tone="info">
                                    {t("page.customer_management.drawer.section.financial", {
                                        defaultValue: "Financial And Loyalty",
                                    })}
                                </Text>

                                <Columns space="small" collapseBelow="desktop">
                                    <Column width="1/2">
                                        <Stack space="small">
                                            <NumericTextField
                                                label={t("page.customer_management.drawer.label.cus_creditlimit", {
                                                    defaultValue: "Credit Limit",
                                                })}
                                                value={formData.cus_creditlimit ?? ""}
                                                tone={errors.cus_creditlimit ? "critical" : undefined}
                                                message={errors.cus_creditlimit}
                                                onChange={(value) => updateNumber("cus_creditlimit", value)}
                                                onInvalid={flagNumbersOnly("cus_creditlimit")}
                                            />
                                            <NumericTextField
                                                label={t("page.customer_management.drawer.label.cus_creditperiod", {
                                                    defaultValue: "Credit Period",
                                                })}
                                                value={formData.cus_creditperiod ?? ""}
                                                tone={errors.cus_creditperiod ? "critical" : undefined}
                                                message={errors.cus_creditperiod}
                                                onChange={(value) => updateNumber("cus_creditperiod", value)}
                                                onInvalid={flagNumbersOnly("cus_creditperiod")}
                                            />
                                            <NumericTextField
                                                label={t("page.customer_management.drawer.label.cus_opbal", {
                                                    defaultValue: "Opening Balance",
                                                })}
                                                value={formData.cus_opbal ?? ""}
                                                disabled={isEditing}
                                                tone={errors.cus_opbal ? "critical" : undefined}
                                                message={errors.cus_opbal}
                                                onChange={(value) => updateNumber("cus_opbal", value)}
                                                onInvalid={flagNumbersOnly("cus_opbal")}
                                            />
                                            <NumericTextField
                                                label={t("page.customer_management.drawer.label.cus_purchase", {
                                                    defaultValue: "Purchase",
                                                })}
                                                value={formData.cus_purchase ?? ""}
                                                tone={errors.cus_purchase ? "critical" : undefined}
                                                message={errors.cus_purchase}
                                                onChange={(value) => updateNumber("cus_purchase", value)}
                                                onInvalid={flagNumbersOnly("cus_purchase")}
                                            />
                                            <NumericTextField
                                                label={t("page.customer_management.drawer.label.cus_returns", {
                                                    defaultValue: "Returns",
                                                })}
                                                value={formData.cus_returns ?? ""}
                                                tone={errors.cus_returns ? "critical" : undefined}
                                                message={errors.cus_returns}
                                                onChange={(value) => updateNumber("cus_returns", value)}
                                                onInvalid={flagNumbersOnly("cus_returns")}
                                            />
                                            <NumericTextField
                                                label={t("page.customer_management.drawer.label.cus_creditnote", {
                                                    defaultValue: "Credit Note",
                                                })}
                                                value={formData.cus_creditnote ?? ""}
                                                tone={errors.cus_creditnote ? "critical" : undefined}
                                                message={errors.cus_creditnote}
                                                onChange={(value) => updateNumber("cus_creditnote", value)}
                                                onInvalid={flagNumbersOnly("cus_creditnote")}
                                            />
                                        </Stack>
                                    </Column>
                                    <Column width="1/2">
                                        <Stack space="small">
                                            <NumericTextField
                                                label={t("page.customer_management.drawer.label.cus_debitnote", {
                                                    defaultValue: "Debit Note",
                                                })}
                                                value={formData.cus_debitnote ?? ""}
                                                tone={errors.cus_debitnote ? "critical" : undefined}
                                                message={errors.cus_debitnote}
                                                onChange={(value) => updateNumber("cus_debitnote", value)}
                                                onInvalid={flagNumbersOnly("cus_debitnote")}
                                            />
                                            <NumericTextField
                                                label={t("page.customer_management.drawer.label.cus_payment", {
                                                    defaultValue: "Payment",
                                                })}
                                                value={formData.cus_payment ?? ""}
                                                tone={errors.cus_payment ? "critical" : undefined}
                                                message={errors.cus_payment}
                                                onChange={(value) => updateNumber("cus_payment", value)}
                                                onInvalid={flagNumbersOnly("cus_payment")}
                                            />
                                            <NumericTextField
                                                label={t("page.customer_management.drawer.label.cus_balance", {
                                                    defaultValue: "Balance",
                                                })}
                                                value={formData.cus_balance ?? ""}
                                                disabled={isEditing}
                                                allowNegative
                                                tone={errors.cus_balance ? "critical" : undefined}
                                                message={errors.cus_balance}
                                                onChange={(value) => updateNumber("cus_balance", value)}
                                                onInvalid={flagNumbersOnly("cus_balance")}
                                            />
                                            <NumericTextField
                                                label={t("page.customer_management.drawer.label.cus_openpoint", {
                                                    defaultValue: "Open Points",
                                                })}
                                                value={formData.cus_openpoint ?? ""}
                                                tone={errors.cus_openpoint ? "critical" : undefined}
                                                message={errors.cus_openpoint}
                                                onChange={(value) => updateNumber("cus_openpoint", value)}
                                                onInvalid={flagNumbersOnly("cus_openpoint")}
                                            />
                                            <NumericTextField
                                                label={t("page.customer_management.drawer.label.cus_earnepoint", {
                                                    defaultValue: "Earned Points",
                                                })}
                                                value={formData.cus_earnepoint ?? ""}
                                                tone={errors.cus_earnepoint ? "critical" : undefined}
                                                message={errors.cus_earnepoint}
                                                onChange={(value) => updateNumber("cus_earnepoint", value)}
                                                onInvalid={flagNumbersOnly("cus_earnepoint")}
                                            />
                                            <NumericTextField
                                                label={t("page.customer_management.drawer.label.cus_redeempoint", {
                                                    defaultValue: "Redeemed Points",
                                                })}
                                                value={formData.cus_redeempoint ?? ""}
                                                tone={errors.cus_redeempoint ? "critical" : undefined}
                                                message={errors.cus_redeempoint}
                                                onChange={(value) => updateNumber("cus_redeempoint", value)}
                                                onInvalid={flagNumbersOnly("cus_redeempoint")}
                                            />
                                            <NumericTextField
                                                label={t("page.customer_management.drawer.label.cus_points", {
                                                    defaultValue: "Current Points",
                                                })}
                                                value={formData.cus_points ?? ""}
                                                tone={errors.cus_points ? "critical" : undefined}
                                                message={errors.cus_points}
                                                onChange={(value) => updateNumber("cus_points", value)}
                                                onInvalid={flagNumbersOnly("cus_points")}
                                            />
                                        </Stack>
                                    </Column>
                                </Columns>

                                <Divider />

                                <Columns space="small" collapseBelow="desktop">
                                    <Column width="1/2">
                                        <Checkbox
                                            id="customer-active"
                                            label={t("page.customer_management.drawer.label.cus_active", {
                                                defaultValue: "Customer Is Active",
                                            })}
                                            checked={formData.cus_active}
                                            onChange={(e) => updateBoolean("cus_active", e.currentTarget.checked)}
                                        />
                                    </Column>
                                    <Column width="1/2">
                                        <Checkbox
                                            id="customer-loyalty-active"
                                            label={t("page.customer_management.drawer.label.cus_loyaltyactive", {
                                                defaultValue: "Loyalty Is Active",
                                            })}
                                            checked={formData.cus_loyaltyactive}
                                            onChange={(e) => updateBoolean("cus_loyaltyactive", e.currentTarget.checked)}
                                        />
                                    </Column>
                                </Columns>
                            </>
                        )}

                        <Actions>
                            <Button variant="ghost" tone="critical" onClick={onClose}>
                                {t("page.customer_management.drawer.button.cancel", { defaultValue: "Cancel" })}
                            </Button>
                            <Button variant="solid" type={isReadOnly ? "button" : "submit"} tone="brandAccent">
                                {isReadOnly
                                    ? t("page.customer_management.drawer.button.view_only", {
                                          defaultValue: "View Only",
                                      })
                                    : submitting
                                    ? t("page.customer_management.drawer.button.saving", {
                                          defaultValue: "Saving...",
                                      })
                                    : customerToEdit
                                      ? t("page.customer_management.drawer.button.update", {
                                            defaultValue: "Update Customer",
                                        })
                                      : t("page.customer_management.drawer.button.create", {
                                            defaultValue: "Create Customer",
                                        })}
                            </Button>
                        </Actions>
                    </Stack>
                </Box>
            </form>
        </Drawer>
    );
};

export default CreateUpdateCustomerDrawer;